import airports from '../data/airports.json';

const CITY_TO_IATA = {
  abidjan: 'ABJ',
  abijan: 'ABJ',
  paris: 'PAR',
  dakar: 'DKR',
  lyon: 'LYS',
  londres: 'LHR',
  london: 'LHR',
  montral: 'YUL',
  montreal: 'YUL',
  duba: 'DXB',
  dubai: 'DXB',
  'new york': 'JFK',
  yamoussoukro: 'ASK',
  ouagadougou: 'OUA',
  bamako: 'BKO',
  cotonou: 'COO',
  accra: 'ACC',
  niamey: 'NIM',
};

const MONTHS = {
  janvier: 1,
  fevrier: 2,
  mars: 3,
  avril: 4,
  mai: 5,
  juin: 6,
  juillet: 7,
  aout: 8,
  septembre: 9,
  octobre: 10,
  novembre: 11,
  decembre: 12,
};

function normalize(text) {
  return (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function inferAirportCode(value) {
  if (!value) return null;

  const normalized = normalize(value);
  if (CITY_TO_IATA[normalized]) return CITY_TO_IATA[normalized];

  const match = airports.find((airport) => {
    const haystack = `${airport.city || ''} ${airport.name || ''} ${airport.iata_code || ''}`.toLowerCase();
    return haystack.includes(normalized);
  });

  return match?.iata_code || null;
}

function parseDateLike(text) {
  const normalized = normalize(text);

  const isoMatch = normalized.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  const dayMonthMatch = normalized.match(/(\d{1,2})\s+(janvier|fevrier|mars|avril|mai|juin|juillet|aout|septembre|octobre|novembre|decembre)/i);
  if (dayMonthMatch) {
    const day = parseInt(dayMonthMatch[1], 10);
    const month = MONTHS[dayMonthMatch[2].toLowerCase()];
    const year = new Date().getFullYear();
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const slashMatch = normalized.match(/(\d{1,2})\s*(?:\/|-)\s*(\d{1,2})\s*(?:\/|-)\s*(\d{2,4})/);
  if (slashMatch) {
    const day = parseInt(slashMatch[1], 10);
    const month = parseInt(slashMatch[2], 10);
    const year = slashMatch[3].length === 2 ? 2000 + parseInt(slashMatch[3], 10) : parseInt(slashMatch[3], 10);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  return null;
}

export function extractSearchParameters(message) {
  const text = normalize(message);
  const params = {
    origin: null,
    destination: null,
    departureDate: null,
    returnDate: null,
    adults: 1,
    children: 0,
    infants: 0,
    travelClass: 'ECONOMY',
    tripType: 'one-way',
    nonStop: false,
  };

  const originMatch = text.match(/(?:de|depuis|partir(?:\s+de|\s+d)?|depart de)\s+([a-z0-9àâäéèêëîïôöùûüç\s-]+?)(?=\s+(?:pour|vers|a|à)\s|$)/i);
  if (originMatch) {
    params.origin = inferAirportCode(originMatch[1].trim()) || originMatch[1].trim();
  }

  const destMatch = text.match(/(?:pour|vers|a|à)\s+([a-z0-9àâäéèêëîïôöùûüç\s-]+?)(?:\s+le\s|\s+avec|\s+pour|\s+en|$)/i);
  if (destMatch) {
    params.destination = inferAirportCode(destMatch[1].trim()) || destMatch[1].trim();
  }

  const departureDate = parseDateLike(text);
  if (departureDate) params.departureDate = departureDate;

  if (/retour|aller retour|aller-retour|roundtrip|retourner/i.test(text)) {
    params.tripType = 'round-trip';
  }

  if (/direct|sans escale|non stop|vol direct/i.test(text)) {
    params.nonStop = true;
  }

  if (/classe affaires|business|affaires/i.test(text)) {
    params.travelClass = 'BUSINESS';
  }

  if (/premiere|first/i.test(text)) {
    params.travelClass = 'FIRST';
  }

  if (/economique|eco/i.test(text)) {
    params.travelClass = 'ECONOMY';
  }

  const adultsMatch = text.match(/(\d+)\s*(?:adultes|adulte|personnes|passagers?)/i);
  if (adultsMatch) {
    params.adults = parseInt(adultsMatch[1], 10);
  }

  const spouseMatch = text.match(/ma femme|mon mari|couple|deux adultes|2 adultes/i);
  if (spouseMatch) {
    params.adults = 2;
  }

  const familyMatch = text.match(/(?:ma femme|mon mari|avec|famille).*(\d+)\s*(?:enfants|enfant)/i);
  if (familyMatch) {
    params.children = parseInt(familyMatch[1], 10);
  }

  const childrenMatch = text.match(/(\d+)\s*(?:enfants|enfant)/i);
  if (childrenMatch) {
    params.children = parseInt(childrenMatch[1], 10);
  }

  const infantsMatch = text.match(/(\d+)\s*(?:bebes|bébés|bébe|bebe|bébés)/i);
  if (infantsMatch) {
    params.infants = parseInt(infantsMatch[1], 10);
  }

  if (/deux enfants|deux enfant/i.test(text)) {
    params.children = 2;
  }

  return params;
}

export function buildSearchPayload(parsed) {
  const payload = {
    originLocationCode: inferAirportCode(parsed.origin) || parsed.origin || 'ABJ',
    destinationLocationCode: inferAirportCode(parsed.destination) || parsed.destination || 'PAR',
    departureDate: parsed.departureDate || new Date().toISOString().slice(0, 10),
    adults: parsed.adults || 1,
    children: parsed.children || 0,
    infants: parsed.infants || 0,
    travelClass: parsed.travelClass || 'ECONOMY',
    nonStop: Boolean(parsed.nonStop),
    currencyCode: 'XOF',
    returnDate: parsed.returnDate || '',
  };

  if (parsed.tripType === 'round-trip') {
    payload.returnDate = parsed.returnDate || '';
  }

  return payload;
}
